import { db } from '../firebase';
import { collection, addDoc, getDocs, query, where, serverTimestamp, getDoc, doc, updateDoc } from 'firebase/firestore';
import { sendEmail } from '../lib/emailService';
import { DisputeRequest } from '../types';
import { getFormattedTimestamp } from '../lib/utils';

export interface Dispute {
  id?: string;
  tenantId: string;
  staffName: string;
  email: string;
  tipDate: string;
  shift: string;
  tipType: 'Cash Tip' | 'POS Tip';
  originalAmount: number;
  correctedAmount: number;
  reason: string;
  explanation?: string;
  employeeNotes?: string;
  managerNotes?: string;
  status: 'Pending' | 'Resolved' | 'Rejected';
  managerComment?: string;
  resolvedBy?: string;
  resolvedAt?: any;
  createdAt?: any;
  type: 'Submission-Correction' | 'Allocation-Correction';
  correctionStatus?: 'Pending' | 'Adjusted';
  originalTipId?: string;
  originalTipType?: 'Cash' | 'Jar' | 'POS';
  posId?: string | null;
  masterTxId?: string;
  docNumber?: string;
  hoursWorked?: number | null;
  staffId?: string;
  description?: string;
  locationId?: string;
  locationName?: string;
}

export const submitNewDisputeRequest = async (request: DisputeRequest) => {
  let masterTransactionData: any = null;
  
  // If targetTransactionId is provided, fetch the master transaction to populate context
  if (request.targetTransactionId) {
    const txRef = doc(db, 'tenants', request.tenantId, 'master_transactions', request.targetTransactionId);
    const txSnapshot = await getDoc(txRef);
    if (txSnapshot.exists()) {
      masterTransactionData = txSnapshot.data();
    }
  }

  const effectiveLocationId = request.locationId || masterTransactionData?.locationId || 'loc-downtown';
  const effectiveLocationName = request.locationName || masterTransactionData?.locationName || 'Downtown Bistro';

  // Map DisputeRequest (input) to the existing Dispute structure (workflow)
  const disputeData: Dispute = {
    tenantId: request.tenantId,
    staffName: masterTransactionData?.staffName || '',
    email: request.email || '',
    posId: request.posId || '',
    tipDate: request.shiftContext?.date || masterTransactionData?.tipDate || '',
    shift: request.shiftContext?.shiftId || masterTransactionData?.shift || '',
    tipType: masterTransactionData?.tipType || 'POS Tip',
    originalAmount: masterTransactionData?.amount || 0,
    correctedAmount: 0, // Should be populated if known, defaults to 0 if not
    reason: request.classification || '',
    explanation: request.notes || '',
    status: 'Pending',
    type: 'Submission-Correction',
    createdAt: serverTimestamp(),
    masterTxId: request.targetTransactionId || null,
    locationId: effectiveLocationId,
    locationName: effectiveLocationName
  };

  const disputesCollection = collection(db, 'tenants', request.tenantId, 'disputes');
  
  return await addDoc(disputesCollection, disputeData);
};

export const submitDispute = async (tenantId: string, posId: string, dispute: Dispute) => {
  const disputesRef = collection(db, 'tenants', tenantId, 'disputes');
  const effectiveLocationId = dispute.locationId || 'loc-downtown';
  const effectiveLocationName = dispute.locationName || 'Downtown Bistro';
  
  // Sanitize all fields to eliminate any undefined values before calling Firestore addDoc()
  const sanitizedPayload: Record<string, any> = {
    tenantId: tenantId,
    posId: posId || dispute.posId || null,
    staffName: dispute.staffName || '',
    email: dispute.email || '',
    tipDate: dispute.tipDate || '',
    shift: dispute.shift || '',
    tipType: dispute.tipType || 'POS Tip',
    originalAmount: Number(dispute.originalAmount) || 0,
    correctedAmount: Number(dispute.correctedAmount) || 0,
    reason: dispute.reason || '',
    explanation: dispute.explanation ?? dispute.description ?? '',
    status: dispute.status || 'Pending',
    type: dispute.type || 'Allocation-Correction',
    correctionStatus: dispute.correctionStatus || 'Pending',
    hoursWorked: dispute.hoursWorked !== undefined && dispute.hoursWorked !== null && !isNaN(Number(dispute.hoursWorked)) ? Number(dispute.hoursWorked) : null,
    staffId: dispute.staffId || null,
    originalTipId: dispute.originalTipId || null,
    originalTipType: dispute.originalTipType || null,
    masterTxId: dispute.masterTxId || null,
    docNumber: dispute.docNumber || null,
    locationId: effectiveLocationId,
    locationName: effectiveLocationName,
    createdAt: serverTimestamp()
  };

  // Strip any lingering undefined properties
  Object.keys(sanitizedPayload).forEach(key => {
    if (sanitizedPayload[key] === undefined) {
      delete sanitizedPayload[key];
    }
  });

  const docRef = await addDoc(disputesRef, sanitizedPayload);

  // Trigger dispute notification emails and in-app messages
  try {
    const adminEmail = 'admin@growezy.ai';
    const subject = `[New Tip Dispute] Submitted by ${dispute.staffName}`;
    const textMsg = `New tip dispute submitted by ${dispute.staffName} (${dispute.email}).\n\nDate: ${dispute.tipDate}\nShift: ${dispute.shift}\nTip Type: ${dispute.tipType}\nOriginal Amount: $${dispute.originalAmount.toFixed(2)}\nRequested Amount: $${dispute.correctedAmount.toFixed(2)}\nReason: ${dispute.reason}\nExplanation: ${dispute.explanation || 'N/A'}`;

    const htmlMsg = `
      <div style="font-family: sans-serif; padding: 20px; color: #1f2937;">
        <h2 style="color: #2563eb; margin-top: 0;">📩 New Tip Dispute Submitted</h2>
        <p>A new tip discrepancy dispute has been logged by <strong>${dispute.staffName}</strong>.</p>
        <table style="width: 100%; border-collapse: collapse; margin: 20px 0; background-color: #f9fafb; border: 1px solid #e5e7eb;">
          <tr style="border-bottom: 1px solid #e5e7eb;"><td style="padding: 10px; font-weight: bold;">Staff Name</td><td style="padding: 10px;">${dispute.staffName} (${dispute.email})</td></tr>
          <tr style="border-bottom: 1px solid #e5e7eb;"><td style="padding: 10px; font-weight: bold;">Shift Date & Name</td><td style="padding: 10px;">${dispute.tipDate} (${dispute.shift})</td></tr>
          <tr style="border-bottom: 1px solid #e5e7eb;"><td style="padding: 10px; font-weight: bold;">Tip Type</td><td style="padding: 10px;">${dispute.tipType}</td></tr>
          <tr style="border-bottom: 1px solid #e5e7eb;"><td style="padding: 10px; font-weight: bold;">Original Amount</td><td style="padding: 10px;">$${dispute.originalAmount.toFixed(2)}</td></tr>
          <tr style="border-bottom: 1px solid #e5e7eb;"><td style="padding: 10px; font-weight: bold;">Requested Correction</td><td style="padding: 10px; font-weight: bold; color: #059669;">$${dispute.correctedAmount.toFixed(2)}</td></tr>
          <tr style="border-bottom: 1px solid #e5e7eb;"><td style="padding: 10px; font-weight: bold;">Reason</td><td style="padding: 10px;">${dispute.reason}</td></tr>
          <tr><td style="padding: 10px; font-weight: bold;">Explanation</td><td style="padding: 10px;">${dispute.explanation || 'None provided'}</td></tr>
        </table>
        <p style="margin-top: 20px;">
          <a href="http://localhost:3000/disputes" style="background-color: #2563eb; color: #ffffff; padding: 10px 18px; text-decoration: none; border-radius: 6px; font-weight: bold; display: inline-block;">Review Pending Disputes</a>
        </p>
      </div>
    `;

    // Send alert to management
    await sendEmail(adminEmail, subject, textMsg, htmlMsg, docRef.id, tenantId);

    // Send receipt confirmation to employee if email is provided
    if (dispute.email && dispute.email !== adminEmail) {
      const empSubject = `[TipValue AI] Tip Dispute Received - ${dispute.tipDate}`;
      const empText = `Hi ${dispute.staffName},\n\nWe received your tip dispute for ${dispute.tipDate} (${dispute.shift}). Management will review your request ($${dispute.correctedAmount.toFixed(2)}) and notify you once processed.\n\nThank you,\nTipValue AI Team`;
      const empHtml = `
        <div style="font-family: sans-serif; padding: 20px; color: #1f2937;">
          <h2 style="color: #059669; margin-top: 0;">Dispute Received</h2>
          <p>Hi <strong>${dispute.staffName}</strong>,</p>
          <p>Your dispute regarding the tip allocation on <strong>${dispute.tipDate}</strong> (${dispute.shift}) has been submitted successfully.</p>
          <p>Requested Correction: <strong>$${dispute.correctedAmount.toFixed(2)}</strong> (Original: $${dispute.originalAmount.toFixed(2)})</p>
          <p>Management will review your request and you will receive an update as soon as it is resolved.</p>
        </div>
      `;
      await sendEmail(dispute.email, empSubject, empText, empHtml, docRef.id, tenantId);
    }

    // Post system message log in tenant messages collection
    await addDoc(collection(db, 'tenants', tenantId, 'messages'), {
      tenantId,
      senderUid: 'system',
      senderName: dispute.staffName,
      senderRole: 'Staff',
      subject: `New Dispute: ${dispute.staffName} - ${dispute.tipDate}`,
      body: textMsg,
      type: 'DisputeAlert',
      status: 'Unread',
      disputeId: docRef.id,
      timestamp: serverTimestamp()
    });
  } catch (notifyErr) {
    console.warn('Failed to dispatch dispute submission notifications:', notifyErr);
  }
};

export const getPendingDisputes = async (tenantId: string): Promise<Dispute[]> => {
  const disputesRef = collection(db, 'tenants', tenantId, 'disputes');
  const q = query(disputesRef, where('status', '==', 'Pending'));
  try {
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({id: doc.id, ...doc.data()} as Dispute));
  } catch (error) {
    console.error("Error fetching pending disputes:", error);
    return [];
  }
};

export const getPendingDisputesByEmail = async (tenantId: string, email: string): Promise<Dispute[]> => {
  const disputesRef = collection(db, 'tenants', tenantId, 'disputes');
  const q = query(disputesRef, where('email', '==', email), where('status', '==', 'Pending'));
  try {
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({id: doc.id, ...doc.data()} as Dispute));
  } catch (error) {
    console.error("Error fetching pending disputes by email:", error);
    return [];
  }
};

export const getDisputes = async (tenantId: string): Promise<Dispute[]> => {
  const disputesRef = collection(db, 'tenants', tenantId, 'disputes');
  const querySnapshot = await getDocs(disputesRef);
  return querySnapshot.docs.map(doc => ({id: doc.id, ...doc.data()} as Dispute));
};

export const getDisputesByEmail = async (tenantId: string, email: string): Promise<Dispute[]> => {
  const disputesRef = collection(db, 'tenants', tenantId, 'disputes');
  const q = query(disputesRef, where('email', '==', email));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => doc.data() as Dispute);
};

export const getDisputeRequestsByEmail = async (tenantId: string, email: string): Promise<DisputeRequest[]> => {
  const disputesRef = collection(db, 'tenants', tenantId, 'dispute_requests');
  const q = query(disputesRef, where('email', '==', email));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({id: doc.id, ...doc.data()} as DisputeRequest));
};

export const getAllDisputeRequests = async (tenantId: string): Promise<DisputeRequest[]> => {
  const disputesRef = collection(db, 'tenants', tenantId, 'dispute_requests');
  const querySnapshot = await getDocs(disputesRef);
  return querySnapshot.docs.map(doc => ({id: doc.id, ...doc.data()} as DisputeRequest));
};

export const getImpactedTransactions = async (tenantId: string, parentTxId: string) => {
  const masterRef = collection(db, 'tenants', tenantId, 'master_transactions');
  const q = query(masterRef, where('parentTxId', '==', parentTxId));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({id: doc.id, ...doc.data()}));
};

export const updateDispute = async (tenantId: string, disputeId: string, updates: Partial<Dispute>) => {
  const disputeRef = doc(db, 'tenants', tenantId, 'disputes', disputeId);
  await updateDoc(disputeRef, {
    ...updates,
    updatedAt: serverTimestamp()
  });
};

export const resolveSupportInquiry = async (
  tenantId: string,
  inquiryId: string,
  inquiry: Dispute,
  managerEmail: string,
  resolutionNotes: string,
  status: 'Resolved' | 'Rejected' = 'Resolved'
) => {
  const disputeRef = doc(db, 'tenants', tenantId, 'disputes', inquiryId);
  await updateDoc(disputeRef, {
    status,
    resolvedBy: managerEmail,
    managerComment: resolutionNotes,
    resolvedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  // Notify employee via email and in-app message
  try {
    const subject = `[Inquiry Update] Your inquiry for ${inquiry.tipDate || 'shift'} has been ${status.toLowerCase()}`;
    const textMsg = `Hi ${inquiry.staffName || 'Team Member'},\n\nYour inquiry regarding "${inquiry.reason}" has been marked as ${status} by management.\n\nManager Feedback:\n${resolutionNotes || 'No additional comment provided.'}\n\nThank you,\nManagement`;
    const htmlMsg = `
      <div style="font-family: sans-serif; padding: 20px; color: #1f2937;">
        <h2 style="color: ${status === 'Resolved' ? '#059669' : '#dc2626'}; margin-top: 0;">Inquiry ${status}</h2>
        <p>Hi <strong>${inquiry.staffName || 'Team Member'}</strong>,</p>
        <p>Your inquiry regarding <strong>${inquiry.reason}</strong> (${inquiry.tipDate || 'General Shift'}) has been reviewed by management.</p>
        <div style="background-color: #f3f4f6; padding: 15px; border-radius: 8px; margin: 15px 0; border-left: 4px solid #2563eb;">
          <p style="margin: 0; font-size: 14px; font-weight: bold; color: #374151;">Manager Notes / Resolution:</p>
          <p style="margin: 5px 0 0 0; font-size: 14px; color: #1f2937;">${resolutionNotes || 'Reviewed and closed.'}</p>
        </div>
      </div>
    `;

    if (inquiry.email) {
      await sendEmail(inquiry.email, subject, textMsg, htmlMsg, inquiryId, tenantId);
    }

    await addDoc(collection(db, 'tenants', tenantId, 'messages'), {
      tenantId,
      senderUid: 'system',
      senderName: 'Management',
      senderRole: 'Manager',
      recipientEmail: inquiry.email,
      subject: `Inquiry ${status}: ${inquiry.reason}`,
      body: `Your inquiry has been marked as ${status}.\n\nNotes: ${resolutionNotes || 'No notes'}`,
      type: 'InquiryResolution',
      status: 'Unread',
      disputeId: inquiryId,
      timestamp: serverTimestamp()
    });
  } catch (err) {
    console.warn('Failed to dispatch inquiry resolution alert:', err);
  }
};

export const replyToSupportInquiry = async (
  tenantId: string,
  inquiryId: string,
  inquiry: Dispute,
  managerEmail: string,
  messageText: string
) => {
  const disputeRef = doc(db, 'tenants', tenantId, 'disputes', inquiryId);
  await updateDoc(disputeRef, {
    managerComment: messageText,
    lastRepliedAt: serverTimestamp(),
    lastRepliedBy: managerEmail,
    updatedAt: serverTimestamp()
  });

  try {
    const subject = `[Inquiry Response] Message from management regarding your inquiry`;
    const textMsg = `Hi ${inquiry.staffName || 'Team Member'},\n\nManagement has responded to your inquiry regarding "${inquiry.reason}":\n\n${messageText}\n\nThank you,\nManagement`;
    const htmlMsg = `
      <div style="font-family: sans-serif; padding: 20px; color: #1f2937;">
        <h2 style="color: #2563eb; margin-top: 0;">Response to Your Inquiry</h2>
        <p>Hi <strong>${inquiry.staffName || 'Team Member'}</strong>,</p>
        <p>Management sent you a response regarding your inquiry on <strong>${inquiry.reason}</strong>:</p>
        <div style="background-color: #eff6ff; padding: 15px; border-radius: 8px; margin: 15px 0; border: 1px solid #bfdbfe;">
          <p style="margin: 0; font-size: 14px; color: #1e3a8a;">${messageText}</p>
        </div>
      </div>
    `;

    if (inquiry.email) {
      await sendEmail(inquiry.email, subject, textMsg, htmlMsg, inquiryId, tenantId);
    }

    await addDoc(collection(db, 'tenants', tenantId, 'messages'), {
      tenantId,
      senderUid: 'system',
      senderName: managerEmail,
      senderRole: 'Manager',
      recipientEmail: inquiry.email,
      subject: `Reply to Inquiry: ${inquiry.reason}`,
      body: messageText,
      type: 'InquiryReply',
      status: 'Unread',
      disputeId: inquiryId,
      timestamp: serverTimestamp()
    });
  } catch (err) {
    console.warn('Failed to dispatch inquiry reply message:', err);
  }
};

